'use strict';

const libPictView = require('pict-view');

const html = String.raw;

// The orchestrator provider (schema/parse/build/push + wizard-free UI state).
const PROVIDER_HASH = 'ContractPackageImportManager';
const P = `_Pict.providers.${PROVIDER_HASH}`;

/**
 * Host page for the bespoke Contract-package importer.
 *
 * A purpose-built, entity-aware screen (not the generic per-entity wizard) that loads a whole
 * Contract -> Project -> BidItem -> LineItem graph in one pass. All behaviour lives in the
 * ContractPackageImportManager provider; this view is the shell + templates over AppData.ContractImportUI.
 * Three stages read top-to-bottom: (1) what to import (new package vs attach to an existing Contract),
 * (2) where the data is (one file — a sheet or workbook — or a file per level), (3) map each level's key + fields,
 * preview the counts, and import.
 */
class ContractPackageImport extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get manager()
	{
		return this.pict.providers[PROVIDER_HASH];
	}

	onBeforeRender(pRenderable)
	{
		if (this.manager) { this.manager.prepare(); }
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable)
	{
		this.pict.CSSMap.injectCSS();
		if (this.manager) { this.manager.mountUploaders(); this.manager.mountMappingAccordion(); this.manager.mountReviewTree(); this.manager.mountContractPicker(); this.manager.mountProjectPicker(); }
		return super.onAfterRender(pRenderable);
	}
}

module.exports = ContractPackageImport;

module.exports.default_configuration =
{
	ViewIdentifier: 'ContractPackageImport',
	DefaultRenderable: 'ContractPackageImportWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
.cpi-head { display: flex; align-items: center; gap: 0.9rem; max-width: 1040px; margin: 0 auto 1.1rem; }
.cpi-head-ic { display: inline-flex; align-items: center; justify-content: center; width: 2.6rem; height: 2.6rem; border-radius: 12px;
	background: var(--theme-color-background-tertiary, #eef1f5); color: var(--theme-color-brand-primary, #156dd1); font-size: 1.2rem; flex: 0 0 auto; }
.cpi-title { font-size: 1.5rem; font-weight: 700; line-height: 1.1; color: var(--theme-color-text-primary, #1f2733); margin: 0; }
.cpi-sub { font-size: 0.92rem; color: var(--theme-color-text-muted, #6b7686); margin: 0.15rem 0 0; max-width: 70ch; }
.cpi-panel { max-width: 1040px; margin: 0 auto; background: var(--theme-color-background-primary, #fff);
	border: 1px solid var(--theme-color-border-light, #eef1f5); border-radius: 12px; padding: 1.1rem 1.2rem; min-height: 60vh; }
.cpi-step { padding: 0.6rem 0 1rem; border-bottom: 1px solid var(--theme-color-border-light, #f0f2f6); }
.cpi-step:last-child { border-bottom: none; }
.cpi-step-label { font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
	color: var(--theme-color-text-muted, #8894a4); margin-bottom: 0.6rem; }
.cpi-seg-group { display: inline-flex; flex-wrap: wrap; gap: 0.4rem; }
.cpi-seg { cursor: pointer; font: inherit; font-size: 0.9rem; font-weight: 600; padding: 0.5rem 0.9rem; border-radius: 8px;
	border: 1px solid var(--theme-color-border-medium, #d5dbe3); background: var(--theme-color-background-primary, #fff);
	color: var(--theme-color-text-secondary, #45596b); display: inline-flex; align-items: center; gap: 0.4rem; }
.cpi-seg:hover { border-color: var(--theme-color-brand-primary, #156dd1); }
.cpi-seg-on { background: var(--theme-color-brand-primary, #156dd1); border-color: var(--theme-color-brand-primary, #156dd1); color: #fff; }
.cpi-picker { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; margin-top: 0.7rem;
	padding: 0.6rem 0.75rem; border-radius: 8px; background: var(--theme-color-background-secondary, #f6f7f9); }
.cpi-picker-hint, .cpi-upload-hint { font-size: 0.84rem; color: var(--theme-color-text-muted, #6b7686); }
.cpi-picker-opt { color: var(--theme-color-text-muted, #8894a4); font-weight: 400; }
.cpi-tmpl-dl { display: flex; align-items: center; gap: 0.45rem; margin-top: 0.7rem; padding: 0.5rem 0.75rem; border-radius: 8px; font-size: 0.86rem;
	background: color-mix(in srgb, var(--theme-color-status-success, #2e7a3a) 9%, transparent); color: var(--theme-color-text-secondary, #45596b); }
.cpi-tmpl-dl i { color: #217346; }
.cpi-tmpl-dl a { color: var(--theme-color-brand-primary, #156dd1); font-weight: 600; text-decoration: none; }
.cpi-tmpl-dl a:hover { text-decoration: underline; }
.cpi-select { font: inherit; font-size: 0.9rem; padding: 0.35rem 0.5rem; border-radius: 6px; max-width: 22rem;
	border: 1px solid var(--theme-color-border-medium, #d5dbe3); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #1f2733); }
.cpi-upload { display: flex; align-items: center; gap: 0.6rem; margin-top: 0.7rem; }
.cpi-upload-sm { margin-top: 0.4rem; }
.cpi-drop { flex: 1; min-width: 18rem; }
.cpi-drop-sm { min-width: 14rem; }
.cpi-level { border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 10px; padding: 0.7rem 0.85rem; margin-bottom: 0.7rem; }
.cpi-level-head { display: flex; align-items: center; gap: 0.55rem; flex-wrap: wrap; }
.cpi-level-ic { color: var(--theme-color-brand-primary, #156dd1); }
.cpi-level-count { font-size: 0.78rem; color: var(--theme-color-text-muted, #8894a4); }
.cpi-map-accordion { margin: 0.2rem 0 0.6rem; }
.cpi-level-body { padding: 0.4rem 0.1rem 0.2rem; }
.cpi-level-controls { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 0.9rem; margin-bottom: 0.9rem;
	padding-bottom: 0.7rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); }
.cpi-level-controls .field { margin-bottom: 0; }
.cpi-field-grid .label.is-small { margin-bottom: 0.2rem; font-weight: 600; }
.cpi-section { margin-bottom: 1.1rem; }
.cpi-section:last-child { margin-bottom: 0.2rem; }
.cpi-section-head { background: var(--theme-color-background-secondary, #f4f6f9); color: var(--theme-color-brand-primary, #4b56d2);
	font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em;
	padding: 0.42rem 0.8rem; border-radius: 6px; margin-bottom: 0.7rem; }
.cpi-key { font-size: 0.85rem; color: var(--theme-color-text-secondary, #45596b); display: inline-flex; align-items: center; gap: 0.4rem; }
.cpi-fields { display: grid; grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr)); gap: 0.35rem 0.9rem; margin-top: 0.6rem; }
.cpi-field { display: flex; align-items: center; gap: 0.4rem; }
.cpi-field-label { font-size: 0.84rem; color: var(--theme-color-text-secondary, #45596b); min-width: 8rem; }
.cpi-req { color: var(--theme-color-status-error, #b62828); }
.cpi-field .cpi-select { flex: 1; min-width: 0; }
.cpi-actions { display: flex; gap: 0.6rem; margin: 0.8rem 0 0.4rem; }
.cpi-btn { cursor: pointer; font: inherit; font-size: 0.9rem; font-weight: 600; padding: 0.5rem 1rem; border-radius: 8px; border: none;
	background: var(--theme-color-brand-primary, #156dd1); color: #fff; display: inline-flex; align-items: center; gap: 0.4rem; }
.cpi-btn:hover { background: var(--theme-color-brand-primaryhover, #1257ab); }
.cpi-btn-ghost { background: transparent; color: var(--theme-color-brand-primary, #156dd1); border: 1px solid var(--theme-color-border-medium, #d5dbe3); }
.cpi-preview { display: flex; align-items: center; gap: 0.45rem; margin: 0.5rem 0; padding: 0.6rem 0.8rem; border-radius: 8px; font-size: 0.92rem;
	background: var(--theme-color-state-success-bg, #e7f6ec); color: var(--theme-color-state-success-text, #1d7a3f); }
.cpi-warn { display: flex; align-items: center; gap: 0.45rem; margin: 0.3rem 0; padding: 0.45rem 0.7rem; border-radius: 8px; font-size: 0.86rem;
	background: var(--theme-color-state-warning-bg, #fff7e6); color: var(--theme-color-state-warning-text, #9a6a00); }
.cpi-tree { border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 10px; padding: 0.5rem 0.7rem; overflow-x: auto; background: var(--theme-color-background-primary, #fff); }
.cpi-tree-node { margin: 0.15rem 0; }
.cpi-tree-node > .cpi-tree-children { margin-left: 1.1rem; padding-left: 0.6rem; border-left: 2px solid var(--theme-color-border-light, #eef1f5); }
.cpi-tree-head { cursor: pointer; display: flex; align-items: center; gap: 0.5rem; padding: 0.32rem 0.4rem; border-radius: 6px; list-style: none; }
.cpi-tree-head:hover { background: var(--theme-color-background-secondary, #f6f7f9); }
.cpi-tree-head::-webkit-details-marker { display: none; }
.cpi-tree-head::before { content: '▸'; color: var(--theme-color-text-muted, #8894a4); font-size: 0.8rem; transition: transform 0.15s; }
details[open] > .cpi-tree-head::before { transform: rotate(90deg); }
.cpi-tree-leaf { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; padding: 0.3rem 0.4rem 0.3rem 1.3rem; }
.cpi-tree-badge { flex: 0 0 auto; font-size: 0.64rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; padding: 0.1rem 0.4rem; border-radius: 999px;
	background: var(--theme-color-background-tertiary, #eef1f5); color: var(--theme-color-brand-primary, #4b56d2); }
.cpi-tree-title { font-weight: 600; color: var(--theme-color-text-primary, #1f2733); font-size: 0.9rem; }
.cpi-tree-sub { font-size: 0.78rem; color: var(--theme-color-text-muted, #8894a4); }
.cpi-tree-fields { display: flex; flex-wrap: wrap; gap: 0.3rem 0.5rem; margin: 0.15rem 0 0.35rem 1.3rem; }
.cpi-tree-chip { font-size: 0.78rem; color: var(--theme-color-text-secondary, #45596b); background: var(--theme-color-background-secondary, #f6f7f9); border-radius: 5px; padding: 0.08rem 0.4rem; }
.cpi-tree-chip-k { color: var(--theme-color-text-muted, #8894a4); }
.cpi-tree-empty { color: var(--theme-color-text-muted, #8894a4); font-size: 0.9rem; padding: 0.6rem; }
.cpi-note { display: flex; align-items: center; gap: 0.45rem; margin: 0.6rem 0 0; padding: 0.5rem 0.75rem; border-radius: 8px; font-size: 0.88rem;
	background: var(--theme-color-background-secondary, #f6f7f9); color: var(--theme-color-text-secondary, #45596b); }
.cpi-result { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; margin: 0 0 0.7rem; padding: 0.6rem 0.8rem; border-radius: 8px; font-size: 0.92rem;
	background: var(--theme-color-state-success-bg, #e7f6ec); color: var(--theme-color-state-success-text, #1d7a3f); }
.cpi-result a { margin-left: 0.2rem; font-weight: 600; color: var(--theme-color-brand-primary, #156dd1); text-decoration: none; }
.cpi-error { display: flex; align-items: center; gap: 0.5rem; margin: 0 0 0.7rem; padding: 0.6rem 0.8rem; border-radius: 8px; font-size: 0.92rem;
	background: var(--theme-color-state-warning-bg, #fdecec); color: var(--theme-color-state-warning-text, #b3261e); }
`,

	Templates:
	[
		{
			Hash: 'CPI-Contract-Option-Template',
			Template: html`<option value="{~D:Record.IDContract~}" data-key="{~D:Record.Key~}" data-label="{~D:Record.Label~}">{~D:Record.Label~}</option>`,
		},
		{
			Hash: 'CPI-Project-Option-Template',
			Template: html`<option value="{~D:Record.IDProject~}" data-key="{~D:Record.Key~}" data-label="{~D:Record.Label~}">{~D:Record.Label~}</option>`,
		},
		{
			Hash: 'CPI-ProjectPicker-Template',
			Template: html`
				<label>Project <span class="cpi-picker-opt">(optional)</span>:
					<div id="CPI-ProjectPicker" class="cpi-picker-mount"></div>
				</label>`,
		},
		{
			Hash: 'CPI-Col-Option-Template',
			Template: html`<option value="{~D:Record.Name~}"{~NE:Record.Selected^ selected~}>{~D:Record.Label~}</option>`,
		},
		{
			Hash: 'CPI-Picker-Template',
			Template: html`
				<div class="cpi-picker">
					<label>Contract:
						<div id="CPI-ContractPicker" class="cpi-picker-mount"></div>
					</label>
					{~TS:CPI-ProjectPicker-Template:AppData.ContractImportUI.ProjectPickerSlot~}
						<span class="cpi-picker-hint">Attach to an existing Contract (and, optionally, a Project) — only the levels below your selection are created. Pick a Project to import just Bid Items &amp; Line Items.</span>
				</div>`,
		},
		{
			Hash: 'CPI-TopUpload-Template',
			Template: html`
				<div class="cpi-upload">
					<div id="CPI-Upload-sheet" class="cpi-drop"></div>
					<span class="cpi-upload-hint">{~D:Record.Hint~}</span>
				</div>`,
		},
		{
			Hash: 'CPI-LevelFile-Template',
			Template: html`
				<div class="cpi-upload cpi-upload-sm">
					<div id="CPI-Upload-{~D:Record.Entity~}" class="cpi-drop cpi-drop-sm"></div>
				</div>`,
		},
		{
			Hash: 'CPI-Tab-Option-Template',
			Template: html`<option value="{~D:Record.Name~}"{~NE:Record.Selected^ selected~}>{~D:Record.Label~}</option>`,
		},
		{
			Hash: 'CPI-LevelTab-Template',
			Template: html`
				<div class="field is-narrow">
					<label class="label is-small">Sheet</label>
					<div class="control">
						<div class="select is-small">
							<select onchange="${P}.setTab('{~D:Record.Entity~}', this.value)">
								{~TS:CPI-Tab-Option-Template:Record.TabItems~}
							</select>
						</div>
					</div>
				</div>`,
		},
		{
			Hash: 'CPI-Field-Template',
			Template: html`
				<div class="column is-half-tablet is-one-quarter-desktop">
					<div class="field">
						<label class="label is-small">{~D:Record.Label~}{~NE:Record.Required^ <span class="cpi-req">*</span>~}</label>
						<div class="control">
							<div class="select is-small is-fullwidth">
								<select onchange="${P}.assignField('{~D:Record.Entity~}', '{~D:Record.Name~}', this.value)">
									{~TS:CPI-Col-Option-Template:Record.ColumnItems~}
								</select>
							</div>
						</div>
					</div>
				</div>`,
		},
		{
			Hash: 'CPI-Group-Template',
			Template: html`
				<div class="cpi-section">
					<div class="cpi-section-head">{~D:Record.Section~}</div>
					<div class="columns is-multiline cpi-field-grid">
						{~TS:CPI-Field-Template:Record.Fields~}
					</div>
				</div>`,
		},
		{
			Hash: 'CPI-Level-Body-Template',
			Template: html`
				<div class="cpi-level-body">
					<div class="cpi-level-controls">
						{~TS:CPI-LevelTab-Template:Record.TabSlot~}
						<div class="field is-narrow">
							<label class="label is-small">Key column</label>
							<div class="control">
								<div class="select is-small">
									<select onchange="${P}.setKeyColumn('{~D:Record.Entity~}', this.value)">
										{~TS:CPI-Col-Option-Template:Record.ColumnItems~}
									</select>
								</div>
							</div>
						</div>
					</div>
					{~TS:CPI-LevelFile-Template:Record.FileSlot~}
					{~TS:CPI-Group-Template:Record.Groups~}
				</div>`,
		},
		{
			Hash: 'CPI-Preview-Template',
			Template: html`<div class="cpi-preview"><i class="fas fa-circle-check"></i> <strong>Ready:</strong> {~D:Record.Summary~}</div>`,
		},
		{
			Hash: 'CPI-Warning-Template',
			Template: html`<div class="cpi-warn"><i class="fas fa-triangle-exclamation"></i> {~D:Record.Message~}</div>`,
		},
		{
			Hash: 'CPI-Note-Template',
			Template: html`<div class="cpi-note"><i class="fas fa-circle-info"></i> {~D:Record.Message~}</div>`,
		},
		{
			Hash: 'CPI-PackageScope-Template',
			Template: html`
				<div class="cpi-seg-group">
					<button class="cpi-seg{~D:AppData.ContractImportUI.ModeNewClass~}" onclick="${P}.setMode('new')"><i class="fas fa-plus"></i> New package</button>
					<button class="cpi-seg{~D:AppData.ContractImportUI.ModeExistingClass~}" onclick="${P}.setMode('existing')"><i class="fas fa-link"></i> Add to an existing Contract</button>
				</div>
				{~TS:CPI-Picker-Template:AppData.ContractImportUI.PickerSlot~}
				<div class="cpi-tmpl-dl"><i class="fas fa-file-excel"></i> <span>New to this? <a href="#" onclick="_Pict.providers.ContractPackageImportManager.downloadTemplate(); return false;">Download the Excel template</a>, fill in the tabs, and upload it below — the columns map themselves.</span></div>`,
		},
		{
			Hash: 'CPI-ItemsIntro-Template',
			Template: html`<div class="cpi-note"><i class="fas fa-circle-info"></i> Upload a file of Bid Items &amp; Line Items. Each row is matched to its existing Contract &amp; Project by a column you'll choose below — so one file can span many projects.</div>`,
		},
		{
			Hash: 'CPI-Link-Template',
			Template: html`
				<div class="cpi-step">
					<div class="cpi-step-label">Link rows to existing Contracts &amp; Projects</div>
					<div class="cpi-picker">
						<label>Contract column:
							<select class="cpi-select" onchange="${P}.setContractKeyColumn(this.value)">
								{~TS:CPI-Col-Option-Template:Record.ContractItems~}
							</select>
						</label>
						<label>Project column:
							<select class="cpi-select" onchange="${P}.setProjectKeyColumn(this.value)">
								{~TS:CPI-Col-Option-Template:Record.ProjectItems~}
							</select>
						</label>
						<span class="cpi-picker-hint">Each row's Contract &amp; Project are matched by these columns (e.g. a contract number and a project name).</span>
					</div>
				</div>`,
		},
		{
			Hash: 'CPI-Result-Template',
			Template: html`<div class="cpi-result"><i class="fas fa-circle-check"></i><span>{~D:Record.Message~}</span><a href="{~D:Record.ViewRoute~}">{~D:Record.ViewLabel~} →</a></div>`,
		},
		{
			Hash: 'CPI-Error-Template',
			Template: html`<div class="cpi-error"><i class="fas fa-triangle-exclamation"></i><span>{~D:Record.Message~}</span></div>`,
		},
		{
			Hash: 'CPI-Review-Template',
			Template: html`
				<div class="cpi-step">
					<div class="cpi-step-label">Review — the records that will be created</div>
					<p class="cpi-picker-hint" style="margin-bottom:0.6rem;">Expand to check the data before importing. Nothing is saved until you click <strong>Import</strong>.</p>
					<div id="CPI-Review-Tree" class="cpi-tree"></div>
				</div>`,
		},
		{
			Hash: 'CPI-Map-Template',
			Template: html`
				<div class="cpi-step">
					<div class="cpi-step-label">3 · Map &amp; import</div>
					<div id="CPI-Map-Accordion" class="cpi-map-accordion"></div>
					<div class="cpi-actions">
						<button class="cpi-btn cpi-btn-ghost" onclick="${P}.buildPreview()"><i class="fas fa-eye"></i> Preview</button>
						<button class="cpi-btn" onclick="${P}.runImport()"><i class="fas fa-cloud-arrow-up"></i> Import package</button>
					</div>
					{~TS:CPI-Preview-Template:AppData.ContractImportUI.PreviewSlot~}
					{~TS:CPI-Warning-Template:AppData.ContractImportUI.WarningSlot~}
				</div>`,
		},
		{
			Hash: 'ContractPackageImport-Template',
			Template: html`
				<section class="section">
					<div class="container">
						<div class="cpi-head">
							<span class="cpi-head-ic"><i class="{~D:AppData.ContractImportUI.HeadIcon~}"></i></span>
							<div>
								<h1 class="cpi-title">{~D:AppData.ContractImportUI.Title~}</h1>
								<p class="cpi-sub">{~D:AppData.ContractImportUI.Subtitle~}</p>
							</div>
						</div>
						<div class="cpi-panel">
							{~TS:CPI-Error-Template:AppData.ContractImportUI.ErrorSlot~}
							{~TS:CPI-Result-Template:AppData.ContractImportUI.ResultSlot~}

							<div class="cpi-step">
								<div class="cpi-step-label">1 · What are you importing?</div>
								{~TS:CPI-PackageScope-Template:AppData.ContractImportUI.PackageScopeSlot~}
								{~TS:CPI-ItemsIntro-Template:AppData.ContractImportUI.ItemsIntroSlot~}
							</div>

							<div class="cpi-step">
								<div class="cpi-step-label">2 · Where's the data?</div>
								<div class="cpi-seg-group">
									<button class="cpi-seg{~D:AppData.ContractImportUI.ShapeSheetClass~}" onclick="${P}.setSourceShape('sheet')"><i class="fas fa-file-lines"></i> One file</button>
									<button class="cpi-seg{~D:AppData.ContractImportUI.ShapeFilesClass~}" onclick="${P}.setSourceShape('files')"><i class="fas fa-copy"></i> Multiple files</button>
								</div>
								{~TS:CPI-TopUpload-Template:AppData.ContractImportUI.TopUploadSlot~}
								{~TS:CPI-Note-Template:AppData.ContractImportUI.NoteSlot~}
							</div>

							{~TS:CPI-Link-Template:AppData.ContractImportUI.LinkSlot~}
							{~TS:CPI-Map-Template:AppData.ContractImportUI.MapSlot~}
							{~TS:CPI-Review-Template:AppData.ContractImportUI.ReviewSlot~}
						</div>
					</div>
				</section>`,
		},
	],

	Renderables:
	[
		{
			RenderableHash: 'ContractPackageImportWrap',
			TemplateHash: 'ContractPackageImport-Template',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
		},
	],
};
